// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStateHook,
	createSelector,
	type HookStateFunction,
} from '@atlassian/react-sweet-state';
import type { OutputSelector } from '@atlassian/react-sweet-state/src/utils/create-selector.tsx';
import { PermissionsStore } from './PermissionsContainer.tsx';
import type { SupportedPermissions } from './SupportedPermissions.tsx';
import type { State } from './State.types.tsx';
import type { Permission } from './PermissionsResponse.types.tsx';

type ProjectId = string;

/**
 * Configuration for permission behavior in different environment contexts
 */
const PERMISSION_CONTEXT_CONFIG: Partial<
	Record<
		SupportedPermissions,
		| {
				/**
				 * When true, permission is explicitly forbidden in collection views
				 */
				forbiddenInCollectionView: boolean;
		  }
		| {
				/**
				 * When true, permission is always granted in collection views
				 */
				alwaysGrantedInCollectionView: boolean;
		  }
		| {
				/**
				 * When true, permission is available in shared views
				 */
				availableInSharedView: boolean;
		  }
	>
> = {
	CREATE_ISSUES: { forbiddenInCollectionView: true },
	ADMINISTER_PROJECTS: { forbiddenInCollectionView: true },

	ADD_COMMENTS: { availableInSharedView: true },
	EDIT_OWN_COMMENTS: { availableInSharedView: true },

	MANAGE_DISCOVERY_VIEWS: { alwaysGrantedInCollectionView: true },
};

const getPermissionConfig = (permissionKey: SupportedPermissions) => {
	return PERMISSION_CONTEXT_CONFIG[permissionKey] ?? {};
};

const isAlwaysGrantedPermission = ({
	isCollectionView,
	permissionKey,
}: {
	isCollectionView: boolean;
	permissionKey: SupportedPermissions;
}) => {
	const config = getPermissionConfig(permissionKey);

	if (
		isCollectionView &&
		'alwaysGrantedInCollectionView' in config &&
		config.alwaysGrantedInCollectionView
	) {
		return true;
	}
};

export type UseHasPermissionsPayload = {
	/**
	 * Permission keys to check for.
	 */
	permissionKeys: SupportedPermissions[] | readonly SupportedPermissions[];

	/**
	 * How to match permission keys,
	 * @option `any` if any of the permissionKeys are marked as true, the result will be true.
	 * @option `all` if all of the permissionKeys are marked as true, the result will be true.
	 * @default 'any'
	 */
	permissionsMatch?: 'any' | 'all';

	/**
	 * Project IDs to check permissions for, if not provided, all projects stored in the state will be checked.
	 */
	projectIds?: ProjectId[];

	/**
	 * How to match project permissions
	 * @option `any` if any of the projects have the permissionKeys, the result will be true.
	 * @option `all` if all of the projects have the permissionKeys, the result will be true.
	 * @default 'all'
	 */
	projectMatch?: 'any' | 'all';
};

const selectViewContext = (state: State) => {
	const {
		isEmbeddedView = false,
		isSharedView = false,
		isCollectionView = false,
	} = state.meta.loadingProps ?? {};

	return { isEmbeddedView, isSharedView, isCollectionView };
};

const selectPermissionsToCheck = (
	state: State,
	props: UseHasPermissionsPayload,
): Permission[][] => {
	const { projectIds, permissionKeys } = props;
	const { isCollectionView = false } = state.meta.loadingProps ?? {};

	// Use contributor permissions for preview mode
	if (state.isPreviewContributorMode) {
		return [state.contributorPermissions];
	}

	const permissions = projectIds
		? projectIds.map((projectId) => state.permissionsByProjectId[projectId] ?? [])
		: Object.values(state.permissionsByProjectId);

	if (permissions.flat().length > 0) {
		return permissions;
	}

	// No permissions in store: check for always-granted permissions in collection view
	if (isCollectionView) {
		const alwaysGrantedPermissions = permissionKeys
			.filter((permissionKey) => isAlwaysGrantedPermission({ permissionKey, isCollectionView }))
			.map((key) => ({ key, havePermission: true }));

		return [alwaysGrantedPermissions];
	}

	return [];
};

type IsPermissionGrantedPayload = {
	isCollectionView: boolean;
	isSharedView: boolean;
	permissionKey: SupportedPermissions;
	grantedKeys: Set<SupportedPermissions>;
};

const isPermissionGranted = ({
	isCollectionView,
	isSharedView,
	permissionKey,
	grantedKeys,
}: IsPermissionGrantedPayload) => {
	const config = getPermissionConfig(permissionKey);

	if (isAlwaysGrantedPermission({ permissionKey, isCollectionView })) {
		return true;
	}

	// Forbidden in collection view
	if (
		isCollectionView &&
		'forbiddenInCollectionView' in config &&
		config.forbiddenInCollectionView
	) {
		return false;
	}

	// Not available in shared view
	if (isSharedView && !('availableInSharedView' in config && config.availableInSharedView)) {
		return false;
	}

	// Check actual permission
	return grantedKeys.has(permissionKey);
};

/**
 * Selector that determines if user has the requested permissions.
 * WARNING: Exported for testing purposes only.
 */
export const selectHasPermissions: OutputSelector<State, UseHasPermissionsPayload, boolean> =
	createSelector(
		selectViewContext,
		selectPermissionsToCheck,
		(_, props) => props,
		(
			viewContext,
			allPermissions,
			{ permissionKeys = [], permissionsMatch = 'any', projectMatch = 'all' },
		) => {
			const { isEmbeddedView, isSharedView, isCollectionView } = viewContext;

			// Global check: embedded views have no permissions or no permissions to check
			if (isEmbeddedView || !allPermissions.flat().length) {
				return false;
			}

			const projectMatchMethod = projectMatch === 'any' ? 'some' : 'every';
			const permissionMatchMethod = permissionsMatch === 'any' ? 'some' : 'every';

			// Check permissions for each project according to projectMatch strategy
			return allPermissions[projectMatchMethod]((permissions) => {
				// Build set of granted permission keys
				const grantedKeys = permissions.reduce((acc, permission) => {
					if (permission.havePermission === true) {
						acc.add(permission.key);
					}
					return acc;
				}, new Set<SupportedPermissions>());

				// Check each permission key with context-aware logic
				return permissionKeys[permissionMatchMethod]((permissionKey) =>
					isPermissionGranted({ isCollectionView, isSharedView, permissionKey, grantedKeys }),
				);
			});
		},
	);

/**
 * Returns true if the user has the requested permissions.
 * @param permissionKeys - The permissions to check for.
 * @param projectMatch - How to match project permissions.
 * @param projectIds - The projects to check permissions for.
 * @param permissionsMatch - How to match permissions.
 * @returns True if the user has the requested permissions.
 *
 * @example
 * ```tsx
 * // Check if user can edit issues in any project
 * const canEdit = useHasPermissions({
 *   permissionKeys: ['EDIT_ISSUES'],
 *   projectMatch: 'any'
 * });
 * ```
 * @example
 * ```tsx
 * // Check if user has both edit AND delete in ALL specified projects
 * const canModify = useHasPermissions({
 *   permissionKeys: ['EDIT_ISSUES', 'DELETE_ISSUES'],
 *   permissionsMatch: 'all',
 *   projectIds: ['100', '200'],
 *   projectMatch: 'all'
 * });
 * ```
 */
export const useHasPermissions: HookStateFunction<boolean, UseHasPermissionsPayload> =
	createStateHook(PermissionsStore, {
		selector: selectHasPermissions,
	});
